/**
 * @file api-orders.ts
 * @description API-клиент заказов (phase10): Order + Shipment + Payment +
 * Return + Customer Timeline.
 */

import type { LegalAcceptanceDto } from "@chaiberi/shared";

import { apiFetch } from "./api-client";
import type { ConsentLegalRevisionDto } from "./api-customers";
// Только тип: реестр статусов заявки живёт в своём модуле, а сюда приезжает
// одним полем сводки. Импорт типом — цикла в рантайме не возникает.
import type {
  DeliveryAgreementStatus,
  DeliveryLockReason,
  OrderDeliveryAgreementDto,
  OrderDeliveryNextStep,
  OrderDeliveryReceiptDto,
} from "./api-delivery-agreement";
// Тоже только тип: «чей ход» считает сервер, а реестр стикеров живёт рядом с
// цепочкой заказа (phase19.24 §14). Цикла в рантайме нет — импорт типом.
import type { OrderChainOwner, OrderChainProblem } from "./order-chain";

// ─── Enums ──────────────────────────────────────────────────────────────────

export type OrderSource = "CRM_MANUAL" | "SITE" | "TELEGRAM" | "AVITO" | "VK" | "PHONE";

/** @deprecated phase10.5 — use OrderStageKind / OrderStageDto.kind. */
export type OrderStatus =
  | "NEW"
  | "CONFIRMED"
  | "AWAITING_PAYMENT"
  | "PAID"
  | "ASSEMBLING"
  | "PACKED"
  | "SHIPPED"
  | "DELIVERED"
  | "COMPLETED"
  | "CANCELLED"
  | "RETURN_REQUESTED"
  | "RETURNED";

/** phase10.5 — системная классификация стадии (определяет автоматику). */
export type OrderStageKind =
  | "CREATED"
  /**
   * phase19.15 (F30) — «доставка по договорённости»: менеджер считает доставку
   * и согласует итог с клиентом. Стадия вне автодвижения F29.
   */
  | "PENDING_DELIVERY_AGREEMENT"
  | "AWAITING_PAYMENT"
  | "PAID"
  | "PACKAGING"
  | "PACKED"
  | "HANDED_TO_CARRIER"
  | "READY_FOR_PICKUP"
  | "IN_TRANSIT"
  | "ARRIVED_AT_PICKUP"
  | "DELIVERED"
  | "COMPLETED"
  | "CANCELLED"
  | "RETURN";

export interface OrderStageDto {
  id: string;
  code: string;
  name: string;
  kind: OrderStageKind;
  color: string | null;
  slaHours: number | null;
  sortOrder: number;
}
export type OrderPaymentStatus = "NONE" | "PARTIAL" | "FULL" | "REFUNDED";
export type OrderShipmentStatus = "NONE" | "PARTIAL" | "FULL" | "RETURNED";

export type PaymentMethod = "CASH" | "CARD" | "PAYKEEPER" | "SBP" | "BANK_TRANSFER";
export type PaymentStatus = "PENDING" | "COMPLETED" | "FAILED" | "REFUNDED";
/**
 * Кем везём конкретную отгрузку (enum `ShipmentCarrier` сервера).
 *
 * Реестр перевозчиков ручного режима: интеграция есть только у СДЭК, остальные
 * менеджер ведёт руками (цена и трек-номер вводятся в админке). Новый
 * перевозчик = строка здесь + строка в `SHIPMENT_CARRIER_LABEL`, ветвлений в
 * компонентах быть не должно.
 */
export type ShipmentCarrier =
  | "PICKUP"
  | "SDEK"
  | "POCHTA"
  | "YANDEX"
  | "PEK"
  | "OZON"
  | "FIVE_POST"
  | "CUSTOM";
export type ShipmentStatus = "PENDING" | "IN_TRANSIT" | "DELIVERED" | "RETURNED";
export type ReturnType = "DEFECT" | "CHANGED_MIND" | "WRONG_ITEM" | "DAMAGED_IN_TRANSIT";
export type ReturnStatus =
  | "REQUESTED"
  | "APPROVED"
  | "AWAITING_GOODS"
  | "INSPECTION"
  | "APPROVED_REFUND"
  | "REJECTED"
  | "COMPLETED";
export type ReturnItemCondition = "LIKE_NEW" | "USED" | "DAMAGED";

// ─── Сводка заказа для карточки реестра (phase19.13) ────────────────────────

/** Кто покупатель: физлицо или юрлицо (влияет на блок документов). */
export type OrderBuyerKind = "PERSON" | "LEGAL_ENTITY";

/** Условия расчёта: предоплата или оплата при получении. */
export type OrderPaymentTerms = "PREPAY" | "POSTPAY";

/** Состояние оплаты в сводке (без REFUNDED — это отдельный сценарий). */
export type OrderPaymentSummaryStatus = "NONE" | "PARTIAL" | "FULL";

/**
 * Куда едет заказ (сводка сервера `buildDeliverySummary`).
 *
 * `CARRIER` — везёт транспортная компания (СДЭК до двери, Почта России, ПЭК);
 * `COURIER` — курьер САМОГО магазина (F18, исход `SHOP_COURIER`). Раньше оба
 * случая приходили как `COURIER`, и заказ Почтой России подписывался
 * «курьер» — дефект ревью 5.1. Новый вид = запись здесь и в
 * `ORDER_DELIVERY_KIND_LABEL`, а не ветка у потребителя.
 */
export type OrderDeliveryKind = "PVZ" | "COURIER" | "CARRIER" | "PICKUP";

/** Перевозчик сводки (пока поддержан только СДЭК). */
export type OrderDeliveryCarrier = "SDEK";

/** Состояние отгрузки в сводке реестра. */
export type OrderDeliveryShipmentStatus =
  | "NONE"
  | "DRAFT"
  | "HANDED"
  | "IN_TRANSIT"
  | "DELIVERED";

/** Состояние документа (счёт / УПД). */
export type OrderDocumentStatus = "NONE" | "DRAFT" | "ISSUED" | "SIGNED";

/** Оплата: сколько получено и чем платили в последний раз. */
export interface OrderPaymentSummaryDto {
  status: OrderPaymentSummaryStatus;
  /** Сумма полученных платежей. */
  paidTotal: number;
  /** Способ последнего платежа: машинный код (`CASH`) или готовая подпись. */
  lastMethod: string | null;
}

/** Доставка: чем, куда, в каком состоянии отгрузка. */
export interface OrderDeliverySummaryDto {
  kind: OrderDeliveryKind | null;
  carrier: OrderDeliveryCarrier | null;
  shipmentStatus: OrderDeliveryShipmentStatus;
  trackNumber: string | null;
}

/** Документы юрлица: счёт и УПД. Для физлица сервер отдаёт `null`. */
export interface OrderDocumentsSummaryDto {
  invoiceStatus: OrderDocumentStatus;
  updStatus: OrderDocumentStatus;
}

// ─── Orders ─────────────────────────────────────────────────────────────────

export interface OrderDto {
  id: string;
  shopId: string;
  number: string;
  /** @deprecated phase10.5 — use stage.kind. */
  status: OrderStatus;
  /** phase10.5 — текущая стадия operational pipeline. */
  stage: OrderStageDto | null;
  pipelineId: string | null;
  paymentStatus: OrderPaymentStatus;
  shipmentStatus: OrderShipmentStatus;
  source: OrderSource;
  customerId: string;
  customerDisplayName: string | null;
  legalEntityId: string | null;
  dealId: string | null;
  ownerUserId: string;
  ownerDisplayName: string | null;
  totalGross: string;
  totalVat: string;
  discountAmount: string;
  currency: string;
  itemsCount: number;
  notes: string | null;
  cancelReason: string | null;
  cancelledAt: string | null;
  confirmedAt: string | null;
  paidAt: string | null;
  shippedAt: string | null;
  deliveredAt: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;

  /*
   * phase19.13 — сводка для карточки реестра. Поля опциональные: сервер
   * добавляет их постепенно, карточка обязана работать и без них.
   */
  /** Телефон клиента (сырой или уже отформатированный). */
  customerPhone?: string | null;
  buyerKind?: OrderBuyerKind;
  paymentTerms?: OrderPaymentTerms;
  payment?: OrderPaymentSummaryDto;
  delivery?: OrderDeliverySummaryDto;
  /** `null` — документов нет (или покупатель физлицо), `undefined` — сервер ещё не отдаёт. */
  documents?: OrderDocumentsSummaryDto | null;

  /*
   * phase19.15 (F30) — заявка «доставка по договорённости».
   */
  /**
   * Состояние заявки для бейджа в реестре и на канбане. `null` — обычный заказ,
   * `undefined` — эндпоинт сводку не отдаёт (карточка заказа, GET /orders/:id).
   */
  deliveryAgreement?: { status: DeliveryAgreementStatus } | null;
  /*
   * phase19.24 §14 — «чей ход» для стикера в реестре, на канбане и в карточке.
   * Считает сервер (стадия × способ получения × условия оплаты), клиент только
   * показывает. `undefined` — эндпоинт поле ещё не отдаёт, стикера нет.
   */
  ownerNow?: OrderChainOwner | null;
  /**
   * Проблема стадии для красного стикера «Нужно решение» в реестре и на
   * канбане (§14). Считает сервер там же, где `ownerNow`; `undefined` —
   * эндпоинт поле ещё не отдаёт, стикера нет.
   */
  problemNow?: OrderChainProblem | null;

  /**
   * phase19.24 шаг 5 (OQ-54) — состав заказа в карточке. Нужен признаку
   * «позиция без товара каталога»: `undefined` — сервер состав не отдаёт.
   */
  items?: OrderItemBriefDto[];

  /** Когда заказ значимо менялся в последний раз (только карточка заказа). */
  lastChangeAt?: string | null;
  /** Что именно поменялось — текст последней значимой записи ленты. */
  lastChangeSummary?: string | null;

  /*
   * phase19.16, партия «Юридические документы» (шаг Б, SB143; проект данных
   * §8; uiux-legal-steps-b-c §2): какие редакции оферты, политики и условий
   * возврата действовали при оформлении на сайте. Только карточка заказа;
   * `undefined` — сервер поле ещё не отдаёт (блок «Принятые условия» скрыт).
   */
  legalAcceptances?: LegalAcceptanceDto[];
  /**
   * Согласие на обработку ПДн, данное при этом заказе (`ConsentRecord.orderId`,
   * решение 2026-10-19 §7.27 (д) п. 2): редакция документа согласия. `null` —
   * записи с этим заказом нет; `undefined` — сервер поле ещё не отдаёт.
   */
  pdConsent?: OrderPdConsentDto | null;
  /**
   * С какого момента магазин записывает редакции при заказе (первая
   * непереносная запись; null — ещё не было). До него пропуск документа в
   * «Принятых условиях» — не нарушение, а отсутствие записи.
   */
  legalAcceptanceSince?: string | null;
}

/** Согласие на обработку ПДн при заказе (`GET /admin/orders/:id` → `pdConsent`). */
export interface OrderPdConsentDto {
  acceptedAt: string;
  revokedAt: string | null;
  /** null — редакция не записана (согласие до ввода редакций). */
  legalRevision: ConsentLegalRevisionDto | null;
}

/**
 * Краткая строка состава в карточке заказа (phase19.24 шаг 5, OQ-54).
 *
 * Нужна одному признаку — «позиция не привязана к товару каталога»
 * (`skuId === null`): полоса «Сейчас» и блок «Что дальше» обязаны увидеть
 * проблему до открытия вкладки «Позиции». Поле опциональное: пока сервер
 * состав в карточке не отдаёт, признака просто нет, и карточка работает как
 * раньше.
 */
export interface OrderItemBriefDto {
  id: string;
  /** `null` — товар каталога удалён или не сопоставлен (Avito-импорт). */
  skuId: string | null;
  name: string;
  quantity: string;
  priceGross: string;
  totalGross: string;
  /** Позиция-услуга «Доставка» — у неё SKU нет по определению. */
  isService?: boolean;
}

export interface CreateOrderItemPayload {
  skuId: string;
  quantity: string;
  priceGross?: string;
  discountAmount?: string;
}

/**
 * Способ получения заказа (phase19.24 шаг 5.1, F30 + OQ-65) — enum сервера.
 *
 * Выбрать в админке можно три: пункт магазина, транспортная компания и
 * курьер магазина. `AGREEMENT` («способ ещё не выставлен») остаётся в enum
 * сервера — так лежат старые заказы и заявки с витрины, — но карточкой выбора
 * не показывается (решение пользователя 2026-09-21).
 *
 * Подписи, обязательные поля и допустимые условия оплаты лежат реестром в
 * `lib/receipt-method.ts` — новый исход = запись там и строка здесь, а не
 * ветка в форме.
 */
export type OrderReceiptMethod = "PICKUP" | "CARRIER" | "SHOP_COURIER" | "AGREEMENT";

/**
 * Способ получения в теле запроса: `POST /admin/orders` и
 * `POST /admin/orders/:id/delivery-agreement/outcome`.
 *
 * Снапшот пункта самовывоза в `deliveryAddress` кладёт сервер (источник тот
 * же, что у сайта), позицию-услугу «Доставка» он же создаёт и удаляет —
 * админка передаёт решение менеджера, а не готовые строки заказа.
 */
export interface OrderReceiptPayload {
  method: OrderReceiptMethod;
  /** Кем везём — только для `CARRIER`. */
  carrier?: ShipmentCarrier;
  /**
   * Стоимость доставки СТРОКОЙ в рублях («450.00»): деньги едут Decimal, и
   * `number` терял бы копейки на разборе JSON (правило «деньги строкой»).
   * `CARRIER` — от 0.01, `SHOP_COURIER` — можно «0.00» (бесплатно).
   */
  deliveryAmount?: string;
  /** Адрес доставки одной строкой (подсказки DaData), для ТК и курьера. */
  address?: string;
  /**
   * Город из подсказки DaData («Челябинск», без типа населённого пункта).
   *
   * Снапшот способа получения перезаписывает `Order.deliveryAddress`, и без
   * этого поля город терялся: расчёт доставки в карточке открывался с пустым
   * городом (находка ревью 5.1). Подсказка не пришла — поле не отправляем:
   * выдуманный город хуже пустого.
   */
  city?: string;
  /** Код города СДЭК, если он известен (из справочника ПВЗ, не из DaData). */
  cityCode?: number;
  /** Комментарий, который увидит клиент в письме. */
  comment?: string;
}

export interface CreateOrderPayload {
  customerId: string;
  legalEntityId?: string;
  dealId?: string;
  source?: OrderSource;
  items: CreateOrderItemPayload[];
  deliveryAddress?: Record<string, unknown>;
  addressId?: string;
  currency?: string;
  notes?: string;
  /*
   * phase19.24 шаг 5.1 — способ получения и условия оплаты прямо при
   * создании: заказ сразу уходит в «Согласование доставки», письмо клиенту
   * шлёт сервер одним действием.
   */
  receipt?: OrderReceiptPayload;
  paymentTerms?: OrderPaymentTerms;
  /** `true` — создать и отправить письмо с кнопкой подтверждения. */
  sendForAgreement?: boolean;
  /**
   * `true` — согласуем по телефону: заявка создаётся, письмо не уходит
   * (у клиента нет e-mail), ссылку менеджер диктует сам. Пометка попадает в
   * заявку, чтобы «письмо не отправлено» не читалось как сбой.
   */
  agreeByPhone?: boolean;
}

/**
 * Созданный заказ: заказ плюс ссылка подтверждения, если заявку завели сразу
 * (`sendForAgreement`/`agreeByPhone`). Ссылку диктуют клиенту по телефону,
 * поэтому она нужна в ответе, а не отдельным запросом.
 */
export interface CreatedOrderDto extends OrderDto {
  agreementLink: string | null;
}

export async function listOrders(params?: {
  search?: string;
  status?: OrderStatus;
  source?: OrderSource;
  customerId?: string;
  ownerUserId?: string;
  openOnly?: boolean;
  /**
   * `me` — только заказы, где сейчас ход менеджера (чип «Мой ход» в реестре,
   * phase19.24 §14). Фильтрует сервер: «чей ход» считается там же, где
   * `ownerNow`.
   */
  owner?: "me";
  /**
   * phase19.31 (SC49) — отбор по стадии воронки заказов (одно значение).
   * Чужая или несуществующая стадия — пустой список, а не ошибка.
   */
  stageId?: string;
  limit?: number;
  offset?: number;
}): Promise<{ items: OrderDto[]; total: number }> {
  const search = new URLSearchParams();
  if (params?.search) search.set("search", params.search);
  if (params?.status) search.set("status", params.status);
  if (params?.stageId) search.set("stageId", params.stageId);
  if (params?.source) search.set("source", params.source);
  if (params?.customerId) search.set("customerId", params.customerId);
  if (params?.ownerUserId) search.set("ownerUserId", params.ownerUserId);
  if (params?.openOnly) search.set("openOnly", "true");
  if (params?.owner) search.set("owner", params.owner);
  if (params?.limit) search.set("limit", String(params.limit));
  if (params?.offset) search.set("offset", String(params.offset));
  const qs = search.toString();
  return apiFetch(`/admin/orders${qs ? `?${qs}` : ""}`);
}

export async function getOrder(id: string): Promise<OrderDto> {
  return apiFetch(`/admin/orders/${id}`);
}

export async function createOrder(payload: CreateOrderPayload): Promise<CreatedOrderDto> {
  return apiFetch(`/admin/orders`, { method: "POST", json: payload });
}

export async function updateOrder(
  id: string,
  payload: { notes?: string; deliveryAddress?: Record<string, unknown>; addressId?: string },
): Promise<OrderDto> {
  return apiFetch(`/admin/orders/${id}`, { method: "PATCH", json: payload });
}

/**
 * Результат привязки позиции к товару каталога (phase19.24 шаг 5, OQ-54).
 *
 * Сервер отдаёт заказ целиком (суммы и состав пересчитаны) и номер задания,
 * если его пересобрали: без второго поля менеджер не понял бы, что отметки
 * кладовщика сброшены.
 */
export interface OrderItemRelinkResultDto {
  order: OrderDto;
  /** id пересобранного задания сборки; `null` — задания не было. */
  pickingTaskId: string | null;
  /**
   * Номер задания (`PICK-2026-000041`) для текста тоста. Поле опционально:
   * контракт шага 5 его не обещает — без номера тост говорит «задание сборки»,
   * а не подставляет пустую строку.
   */
  pickingTaskNumber?: string | null;
}

/**
 * Привязать (или заменить) позицию заказа товаром каталога.
 *
 * Цена позиции сохраняется сервером (F26): менять её привязкой нельзя, иначе
 * оплаченный заказ поедет по сумме. Ошибки: 409 `order_item_already_linked`
 * (позицию уже привязали в соседней вкладке), 409 `order_item_relink_stage`
 * (стадия не та), 404 (позиции или заказа нет).
 */
export async function relinkOrderItem(
  orderId: string,
  itemId: string,
  skuId: string,
): Promise<OrderItemRelinkResultDto> {
  return apiFetch<OrderItemRelinkResultDto>(
    `/admin/orders/${orderId}/items/${itemId}/relink`,
    { method: "PATCH", json: { skuId } },
  );
}

export const orderAction = {
  confirm: (id: string) =>
    apiFetch<OrderDto>(`/admin/orders/${id}/confirm`, { method: "POST" }),
  awaitPayment: (id: string) =>
    apiFetch<OrderDto>(`/admin/orders/${id}/await-payment`, { method: "POST" }),
  startAssembling: (id: string) =>
    apiFetch<OrderDto>(`/admin/orders/${id}/start-assembling`, { method: "POST" }),
  pack: (id: string) => apiFetch<OrderDto>(`/admin/orders/${id}/pack`, { method: "POST" }),
  complete: (id: string) =>
    apiFetch<OrderDto>(`/admin/orders/${id}/complete`, { method: "POST" }),
  cancel: (id: string, reason: string) =>
    apiFetch<OrderDto>(`/admin/orders/${id}/cancel`, {
      method: "POST",
      json: { reason },
    }),
};

// ─── Shipments ──────────────────────────────────────────────────────────────

/** Откуда взялся тариф в отгрузке/позиции доставки (phase19.12). */
export type DeliveryTariffSource = "CALCULATED" | "MANUAL";

/** Куда везём: пункт выдачи или дверь клиента (phase19.12). */
export type DeliveryDestinationMode = "PVZ" | "COURIER";

/**
 * Место (коробка) отгрузки — phase19.12 шаг 5. Мест может быть несколько,
 * дробление одной позиции между местами запрещено (решение пользователя).
 */
export interface ShipmentPackageDto {
  id: string;
  number: number;
  weightG: number;
  lengthMm: number | null;
  widthMm: number | null;
  heightMm: number | null;
  comment: string | null;
  barcode: string | null;
}

/** Место в теле запроса — сервер заменяет список мест целиком. */
export interface ShipmentPackageInput {
  number: number;
  weightG: number;
  lengthMm?: number | null;
  widthMm?: number | null;
  heightMm?: number | null;
  comment?: string | null;
  /** Раскладка позиций по местам; позиция может быть ровно в одном месте. */
  orderItemIds?: string[];
}

export interface ShipmentDto {
  id: string;
  orderId: string;
  orderNumber: string;
  /**
   * Номер документа (`SH-2026-000045`) — решение пользователя 2026-09-21 по §11 п.1:
   * до него у отгрузки номера не было, и узел «Пути заказа» ссылался на строку
   * вкладки. `undefined` — сервер нумератор ещё не выкатил.
   */
  number?: string | null;

  status: ShipmentStatus;
  carrier: ShipmentCarrier;
  trackNumber: string | null;
  shippedAt: string | null;
  deliveredAt: string | null;
  itemsCount: number;
  notes: string | null;
  createdAt: string;
  /**
   * UUID заявки у перевозчика: задан — отгрузка уже передана, повторная
   * передача запрещена (phase19.12 шаг 6).
   */
  externalCarrierUuid: string | null;
  // ── phase19.12 — тариф и места ────────────────────────────────────────────
  tariffCode: number | null;
  tariffName: string | null;
  /** Код delivery_mode перевозчика (склад-склад / склад-дверь и т. д.). */
  deliveryMode: number | null;
  /** Цена перевозчика без наценки магазина. */
  carrierPrice: number | null;
  tariffQuotedAt: string | null;
  tariffSource: DeliveryTariffSource | null;
  pickupPointCode: string | null;
  packages: ShipmentPackageDto[];
}

export async function listShipments(orderId: string): Promise<ShipmentDto[]> {
  const r = await apiFetch<ShipmentDto[]>(`/admin/orders/${orderId}/shipments`);
  return r;
}

/** Тариф + ПВЗ + места — общая часть create/patch отгрузки (phase19.12). */
export interface ShipmentDeliveryPayload {
  /**
   * Трек-номер руками (phase19.15) — только для перевозчиков без интеграции.
   * Сервер принимает 1..64 символа; пустую строку отклоняет.
   */
  trackNumber?: string;
  pickupPointCode?: string | null;
  tariffCode?: number | null;
  tariffName?: string | null;
  deliveryMode?: number | null;
  carrierPrice?: number | null;
  tariffQuotedAt?: string | null;
  tariffSource?: DeliveryTariffSource;
  packages?: ShipmentPackageInput[];
}

export async function createShipment(
  orderId: string,
  payload: ShipmentDeliveryPayload & {
    /** Без перевозчика сервер берёт его из согласованных условий доставки. */
    carrier?: ShipmentCarrier;
    trackNumber?: string;
    weightG?: number;
    dimensionsMm?: { length: number; width: number; height: number };
    items: { orderItemId: string; quantity: string; datamatrix?: string }[];
    notes?: string;
  },
): Promise<ShipmentDto> {
  return apiFetch(`/admin/orders/${orderId}/shipments`, {
    method: "POST",
    json: payload,
  });
}

/**
 * Обновить отгрузку: ПВЗ, тариф, места (phase19.12).
 * Список мест заменяется целиком. 422 — отгрузка уже передана в СДЭК.
 */
export async function updateShipment(
  shipmentId: string,
  payload: ShipmentDeliveryPayload,
): Promise<ShipmentDto> {
  return apiFetch(`/admin/shipments/${shipmentId}`, {
    method: "PATCH",
    json: payload,
  });
}

/**
 * Удалить отгрузку-черновик (phase19.12).
 *
 * Позиции возвращаются в заказ. 422 `shipment_not_draft` — отгрузка уже
 * передана перевозчику, удалять её нельзя; 404 — уже удалена.
 */
export async function deleteShipment(shipmentId: string): Promise<void> {
  await apiFetch(`/admin/shipments/${shipmentId}`, { method: "DELETE" });
}

export const shipmentAction = {
  ship: (id: string) =>
    apiFetch<ShipmentDto>(`/admin/shipments/${id}/ship`, { method: "POST" }),
  deliver: (id: string) =>
    apiFetch<ShipmentDto>(`/admin/shipments/${id}/deliver`, { method: "POST" }),
};

// ─── Доставка по заказу (phase19.12 шаг 5) ──────────────────────────────────

/** Адрес доставки заказа: известные поля + произвольные (JSON из заказа). */
export interface OrderDeliveryAddressDto {
  city?: string;
  cityCode?: number;
  postalCode?: string;
  address?: string;
  pickupPointCode?: string;
  mode?: DeliveryDestinationMode;
  [key: string]: unknown;
}

/** Позиция-услуга «Доставка» в заказе: что клиент видел и за что заплатил. */
export interface OrderDeliveryItemDto {
  tariffCode: number | null;
  tariffName: string | null;
  mode: DeliveryDestinationMode | null;
  /** Цена для клиента (0 = бесплатная доставка). */
  priceGross: number;
  /** Цена перевозчика без наценки — расход магазина. */
  carrierPrice: number | null;
  periodMin: number | null;
  periodMax: number | null;
  quotedAt: string | null;
  source: DeliveryTariffSource;
}

/**
 * Товарная позиция заказа для раскладки по местам.
 *
 * Вес и габариты — из карточки товара, за одну штуку. null значит «не
 * заполнено в товаре»: по такой позиции авто-расчёт места не работает, и
 * менеджер должен это видеть (phase19.12 шаг 5).
 */
export interface OrderDeliveryLineDto {
  orderItemId: string;
  skuId: string;
  name: string;
  quantity: number;
  weightG: number | null;
  lengthMm: number | null;
  widthMm: number | null;
  heightMm: number | null;
}

/**
 * Всё о доставке заказа одним запросом (phase19.24 шаг 5.2 §6 п. 2).
 *
 * Блок «Доставка» в карточке не собирает картину из трёх ответов: способ,
 * порядок оплаты, заявка и право на правку приходят вместе — иначе экран
 * успевал показать «можно менять», пока грузился признак оплаты.
 *
 * Снапшота расчёта может не быть (`receipt.quote === null`) — это нормальное
 * состояние «сумму поставили руками», а не сбой.
 */
/** Получатель для перевозчика: кому звонить и на чьё имя заявка. */
export interface OrderDeliveryRecipientDto {
  name: string | null;
  phone: string | null;
  email: string | null;
}

export interface OrderDeliveryDto {
  /** Клиент заказа как получатель (сайдбар «Новая отгрузка», 2026-09-22). */
  recipient?: OrderDeliveryRecipientDto | null;
  /** Чего не хватает получателю под способ получения (F16, шаг 5.3) — подсветка менеджеру. */
  recipientIssues?: { code: string; message: string }[];
  deliveryAddress: OrderDeliveryAddressDto | null;
  deliveryItem: OrderDeliveryItemDto | null;
  items: OrderDeliveryLineDto[];

  /** Снапшот способа получения: что, куда, за сколько и чьими руками выставлено. */
  receipt: OrderDeliveryReceiptDto | null;
  /** Как платит клиент — часть предмета согласования (F31). */
  paymentTerms: OrderPaymentTerms;
  /** Заявка F30 в урезанном виде: статус, сроки и ссылка подтверждения. */
  agreement: OrderDeliveryAgreementDto;
  /** Можно ли прямо сейчас менять условия. */
  canEdit: boolean;
  /** Почему нельзя (см. `DeliveryLockReason`); `null` — можно. */
  lockReason: DeliveryLockReason | null;
  /**
   * Единственный путь дальше, когда правка закрыта стадией (`lockReason:
   * "stage"`): `open_agreement` — сначала перевести заказ в согласование
   * доставки. `null` — пути нет, блок только читают. Поле опционально: старые
   * сборки сервера отвечают без него.
   */
  nextStep?: OrderDeliveryNextStep | null;
  /**
   * Перевозчики, у которых в системе есть калькулятор доставки (сегодня СДЭК).
   *
   * Решение пользователя 2026-09-21 (§8): блок расчёта показываем ТОЛЬКО для
   * них. Для остальных о калькуляторе не упоминаем вовсе — у менеджера есть
   * поля адреса и ручной стоимости, и плашка «расчёт недоступен» была бы шумом.
   *
   * Здоровье интеграции на список НЕ влияет (правка 2026-09-22): нехватку
   * ключей или настроек объясняет сам расчёт причиной со ссылкой в настройки.
   */
  integratedCarriers: ShipmentCarrier[];
}

/** Что клиент выбрал на витрине + позиции для раскладки по местам. */
export async function getOrderDelivery(orderId: string): Promise<OrderDeliveryDto> {
  return apiFetch(`/admin/orders/${orderId}/delivery`);
}

// ─── Payments ───────────────────────────────────────────────────────────────

export interface PaymentDto {
  id: string;
  orderId: string;
  orderNumber: string;
  /**
   * Номер документа (`PAY-2026-000112`) — решение пользователя 2026-09-21 по §11 п.1:
   * до него у платежа номера не было, и узел «Пути заказа» ссылался на строку
   * вкладки. `undefined` — сервер нумератор ещё не выкатил.
   */
  number?: string | null;

  method: PaymentMethod;
  status: PaymentStatus;
  amount: string;
  currency: string;
  paidAt: string | null;
  refundedAt: string | null;
  refundedAmount: string | null;
  refundReason: string | null;
  notes: string | null;
  createdAt: string;
}

export async function listPayments(orderId: string): Promise<PaymentDto[]> {
  return apiFetch(`/admin/orders/${orderId}/payments`);
}

export async function recordPayment(
  orderId: string,
  payload: {
    method: PaymentMethod;
    amount: string;
    externalTransactionId?: string;
    notes?: string;
  },
): Promise<PaymentDto> {
  return apiFetch(`/admin/orders/${orderId}/payments`, {
    method: "POST",
    json: payload,
  });
}

export async function refundPayment(
  id: string,
  payload: { amount?: string; reason: string },
): Promise<PaymentDto> {
  return apiFetch(`/admin/payments/${id}/refund`, { method: "POST", json: payload });
}

// ─── Returns ────────────────────────────────────────────────────────────────

export interface ReturnDto {
  id: string;
  orderId: string;
  orderNumber: string;
  /**
   * Номер документа (`RET-2026-000007`) — решение пользователя 2026-09-21 по §11 п.1:
   * до него у возврата номера не было, и узел «Пути заказа» ссылался на строку
   * вкладки. `undefined` — сервер нумератор ещё не выкатил.
   */
  number?: string | null;

  type: ReturnType;
  status: ReturnStatus;
  customerComment: string | null;
  internalComment: string | null;
  refundAmount: string | null;
  requestedAt: string;
  approvedAt: string | null;
  completedAt: string | null;
  rejectedReason: string | null;
  itemsCount: number;
  createdAt: string;
}

export async function listReturns(orderId: string): Promise<ReturnDto[]> {
  return apiFetch(`/admin/orders/${orderId}/returns`);
}

export async function createReturn(
  orderId: string,
  payload: {
    type: ReturnType;
    customerComment?: string;
    internalComment?: string;
    items: {
      orderItemId: string;
      quantity: string;
      condition?: ReturnItemCondition;
      unitRefund?: string;
    }[];
  },
): Promise<ReturnDto> {
  return apiFetch(`/admin/orders/${orderId}/returns`, {
    method: "POST",
    json: payload,
  });
}

export const returnAction = {
  approve: (id: string) =>
    apiFetch<ReturnDto>(`/admin/returns/${id}/approve`, { method: "POST" }),
  inspect: (id: string) =>
    apiFetch<ReturnDto>(`/admin/returns/${id}/inspect`, { method: "POST" }),
  approveRefund: (id: string) =>
    apiFetch<ReturnDto>(`/admin/returns/${id}/approve-refund`, { method: "POST" }),
  complete: (id: string) =>
    apiFetch<ReturnDto>(`/admin/returns/${id}/complete`, { method: "POST" }),
  reject: (id: string, reason: string) =>
    apiFetch<ReturnDto>(`/admin/returns/${id}/reject`, {
      method: "POST",
      json: { reason },
    }),
};

// ─── Customer Timeline ──────────────────────────────────────────────────────

export type TimelineActivityKind =
  | "NOTE" | "TASK" | "CALL" | "EMAIL" | "SMS" | "MEETING"
  | "ORDER_CREATED" | "ORDER_CONFIRMED" | "PAYMENT_RECEIVED"
  | "SHIPMENT_SENT" | "SHIPMENT_DELIVERED" | "ORDER_COMPLETED" | "ORDER_CANCELLED"
  | "RETURN_REQUESTED" | "RETURN_COMPLETED"
  | "NOTIFICATION_SENT" | "FISCAL_RECEIPT_PUNCHED";

export interface TimelineItem {
  id: string;
  kind: TimelineActivityKind;
  title: string;
  content: string | null;
  orderId: string | null;
  shipmentId: string | null;
  paymentId: string | null;
  returnId: string | null;
  payload: unknown;
  createdById: string;
  createdBy?: { displayName: string | null };
  createdAt: string;
}

// ─── Лента заказа (phase19.31, cabinet-api-contract.md §9) ──────────────────

/** Фильтр ленты — чипы «Все / Стадии / Уведомления / Заметки». */
export type OrderFeedFilter = "all" | "stages" | "notifications" | "notes";

/**
 * Исход канала уведомления: `sent` — ушло клиенту; `mock` — канал в тестовом
 * режиме, клиенту НЕ ушло; `failed` — не ушло (`reason: "queued_retry"` —
 * сервер повторит); `skipped` — не отправлялось (`channel_unconfigured`…).
 */
export type OrderFeedChannelStatus = "sent" | "mock" | "failed" | "skipped";

export interface OrderFeedChannelDto {
  /** `IN_APP` | `EMAIL` | `SMS` | `TELEGRAM` (в старых записях ещё `PUSH`). */
  channel: string;
  status: OrderFeedChannelStatus;
  reason: string | null;
}

/** Вид записи журнала (`ActivityKind` сервера). */
export type OrderFeedActivityKind = TimelineActivityKind | "CHAT_MESSAGE";

/** Запись ленты заказа — зеркало `OrderFeedItemDto` сервера. */
export interface OrderFeedItemDto {
  /** `stage:<id>` | `activity:<id>`. */
  id: string;
  at: string;
  type: "stage" | "activity";
  kind: "STAGE_CHANGED" | OrderFeedActivityKind;
  category: "stage" | "notification" | "note" | "other";
  /** null — «Система»: служебный пользователь и все уведомления клиенту. */
  actor: { id: string; displayName: string | null } | null;
  /** Комментарий к системному событию (менеджера или клиента). */
  comment: string | null;
  stage?: { fromName: string | null; toName: string; toKind: OrderStageKind; note: string | null };
  notification?: { templateKind: string; channels: OrderFeedChannelDto[] };
  payment?: { amount: string | null; currency: string | null; method: PaymentMethod | null };
  return?: { number: string | null; type: ReturnType | null };
  shipment?: { number: string | null; carrier: ShipmentCarrier | null; trackNumber: string | null };
  /** Записи, написанные человеком: заметка, задача, звонок, письмо, сообщение. */
  note?: { title: string; content: string | null };
  orderId: string;
  orderNumber: string;
}

/** Счётчики по всей ленте заказа — от фильтра и курсора не зависят. */
export interface OrderFeedCountersDto {
  all: number;
  stages: number;
  notifications: number;
  notes: number;
  other: number;
}

export interface OrderFeedPageDto {
  items: OrderFeedItemDto[];
  /** Курсор следующей (более старой) страницы; null — дальше событий нет. */
  nextCursor: string | null;
  counters: OrderFeedCountersDto;
}

/** `GET /admin/orders/:id/feed` — страница ленты, новые сверху. */
export async function getOrderFeed(
  orderId: string,
  params?: { kind?: OrderFeedFilter; before?: string; limit?: number },
): Promise<OrderFeedPageDto> {
  const search = new URLSearchParams();
  if (params?.kind && params.kind !== "all") search.set("kind", params.kind);
  if (params?.before) search.set("before", params.before);
  if (params?.limit) search.set("limit", String(params.limit));
  const qs = search.toString();
  return apiFetch(`/admin/orders/${orderId}/feed${qs ? `?${qs}` : ""}`);
}

// ─── Stats / Dashboard ──────────────────────────────────────────────────────

export interface OrderStatsDto {
  gmv: string;
  ordersCount: number;
  completedCount: number;
  avgCheck: string;
  cancelledCount: number;
  returnsRate: number;
  openOrdersCount: number;
  revenue7d: string;
  revenue30d: string;
}

export interface TopSkuDto {
  skuId: string | null;
  productName: string;
  /** phase19.31 (SC49) — подпись фасовки («50 г»); null — неизвестна. */
  skuLabel: string | null;
  qtySold: number;
  revenue: string;
}

export async function getOrderStats(days: number = 30): Promise<OrderStatsDto> {
  return apiFetch(`/admin/orders/stats?days=${days}`);
}

export async function getTopSkus(limit: number = 10, days: number = 30): Promise<TopSkuDto[]> {
  return apiFetch(`/admin/orders/stats/top-skus?limit=${limit}&days=${days}`);
}

// ─── SKU picker ─────────────────────────────────────────────────────────────

export interface OrderSkuDto {
  id: string;
  code: string;
  displayName: string;
  priceGross: string;
  vatRate: number;
  vatIncluded: boolean;
  saleUnit: string | null;
  saleQuantity: string | null;
  outOfStock: boolean;
  gtin: string | null;
  productId: string;
  productName: string;
}

export async function listSkusForPicker(params?: {
  search?: string;
  limit?: number;
}): Promise<OrderSkuDto[]> {
  const qs = new URLSearchParams();
  if (params?.search) qs.set("search", params.search);
  if (params?.limit) qs.set("limit", String(params.limit));
  const s = qs.toString();
  return apiFetch(`/admin/orders/skus${s ? `?${s}` : ""}`);
}

export async function getCustomerTimeline(
  customerId: string,
  params?: { kinds?: TimelineActivityKind[]; limit?: number; offset?: number },
): Promise<{ items: TimelineItem[]; total: number }> {
  const search = new URLSearchParams();
  if (params?.kinds && params.kinds.length > 0)
    search.set("kinds", params.kinds.join(","));
  if (params?.limit) search.set("limit", String(params.limit));
  if (params?.offset) search.set("offset", String(params.offset));
  const qs = search.toString();
  return apiFetch(`/admin/customers/${customerId}/timeline${qs ? `?${qs}` : ""}`);
}

// ─── Labels ─────────────────────────────────────────────────────────────────

/** @deprecated phase10.5 — для совместимости с phase10 UI пока остаётся. */
export const ORDER_STATUS_LABEL: Record<OrderStatus, string> = {
  NEW: "Новый",
  CONFIRMED: "Подтверждён",
  AWAITING_PAYMENT: "Ожидает оплаты",
  PAID: "Оплачен",
  ASSEMBLING: "Сборка",
  PACKED: "Упакован",
  SHIPPED: "Отгружен",
  DELIVERED: "Доставлен",
  COMPLETED: "Завершён",
  CANCELLED: "Отменён",
  RETURN_REQUESTED: "Возврат запрошен",
  RETURNED: "Возвращён",
};

/** @deprecated phase10.5 — для совместимости с phase10 UI пока остаётся. */
export const ORDER_STATUS_TONE: Record<OrderStatus, "default" | "secondary" | "destructive" | "outline"> = {
  NEW: "outline",
  CONFIRMED: "outline",
  AWAITING_PAYMENT: "secondary",
  PAID: "secondary",
  ASSEMBLING: "secondary",
  PACKED: "secondary",
  SHIPPED: "default",
  DELIVERED: "default",
  COMPLETED: "default",
  CANCELLED: "destructive",
  RETURN_REQUESTED: "destructive",
  RETURNED: "destructive",
};

/** phase10.5 — fallback-метки kind'ов (магазин может задать своё name). */
export const ORDER_STAGE_KIND_LABEL: Record<OrderStageKind, string> = {
  CREATED: "Заявка",
  PENDING_DELIVERY_AGREEMENT: "Согласование доставки",
  AWAITING_PAYMENT: "Ожидание оплаты",
  PAID: "Оплачен",
  PACKAGING: "На сборке",
  PACKED: "Собран",
  HANDED_TO_CARRIER: "Передан в ТК",
  READY_FOR_PICKUP: "Готов к самовывозу",
  IN_TRANSIT: "В пути",
  ARRIVED_AT_PICKUP: "Прибыл в ПВЗ",
  DELIVERED: "Доставлен",
  COMPLETED: "Завершён",
  CANCELLED: "Отменён",
  RETURN: "Возврат",
};

export const ORDER_STAGE_KIND_TONE: Record<
  OrderStageKind,
  "default" | "secondary" | "destructive" | "outline"
> = {
  CREATED: "outline",
  PENDING_DELIVERY_AGREEMENT: "secondary",
  AWAITING_PAYMENT: "secondary",
  PAID: "secondary",
  PACKAGING: "secondary",
  PACKED: "secondary",
  HANDED_TO_CARRIER: "default",
  READY_FOR_PICKUP: "default",
  IN_TRANSIT: "default",
  ARRIVED_AT_PICKUP: "default",
  DELIVERED: "default",
  COMPLETED: "default",
  CANCELLED: "destructive",
  RETURN: "destructive",
};

/** phase10.5 — универсальный переход по toStageId (drag-drop kanban). */
export async function transitionOrderToStage(
  orderId: string,
  input: { toStageId: string; note?: string; cancelReason?: string },
): Promise<OrderDto> {
  return apiFetch(`/admin/orders/${orderId}/transition`, {
    method: "POST",
    json: input,
  });
}

export interface OrderPipelineWithStagesDto {
  id: string;
  code: string;
  name: string;
  description: string | null;
  isDefault: boolean;
  isActive: boolean;
  stages: (OrderStageDto & { isActive: boolean; ordersCount: number })[];
}

/** phase10.5 — список pipeline'ов магазина для Kanban / Настроек. */
export async function listOrderPipelines(): Promise<
  OrderPipelineWithStagesDto[]
> {
  return apiFetch("/admin/orders/pipelines");
}

// ─── Order Stages — настройка (phase10.5 шаг 8) ──────────────────────────────

/**
 * Полное DTO стадии для страницы настроек (OrderStagesPage). В отличие от
 * упрощённого `OrderStageDto`, содержит isActive / ordersCount / timestamps.
 */
export interface OrderStageConfigDto {
  id: string;
  pipelineId: string;
  code: string;
  name: string;
  kind: OrderStageKind;
  color: string | null;
  slaHours: number | null;
  sortOrder: number;
  isActive: boolean;
  ordersCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateOrderStagePayload {
  code: string;
  name: string;
  kind: OrderStageKind;
  color?: string;
  slaHours?: number;
  sortOrder?: number;
}

export interface UpdateOrderStagePayload {
  code?: string;
  name?: string;
  kind?: OrderStageKind;
  color?: string;
  slaHours?: number;
  sortOrder?: number;
  isActive?: boolean;
}

export async function listOrderStages(
  pipelineId: string,
): Promise<OrderStageConfigDto[]> {
  return apiFetch(`/admin/orders/pipelines/${pipelineId}/stages`);
}

export async function createOrderStage(
  pipelineId: string,
  payload: CreateOrderStagePayload,
): Promise<OrderStageConfigDto> {
  return apiFetch(`/admin/orders/pipelines/${pipelineId}/stages`, {
    method: "POST",
    json: payload,
  });
}

export async function updateOrderStage(
  pipelineId: string,
  stageId: string,
  payload: UpdateOrderStagePayload,
): Promise<OrderStageConfigDto> {
  return apiFetch(
    `/admin/orders/pipelines/${pipelineId}/stages/${stageId}`,
    { method: "PATCH", json: payload },
  );
}

export async function deleteOrderStage(
  pipelineId: string,
  stageId: string,
): Promise<void> {
  await apiFetch(
    `/admin/orders/pipelines/${pipelineId}/stages/${stageId}`,
    { method: "DELETE" },
  );
}

export async function reorderOrderStages(
  pipelineId: string,
  items: { id: string; sortOrder: number }[],
): Promise<OrderStageConfigDto[]> {
  return apiFetch(
    `/admin/orders/pipelines/${pipelineId}/stages/reorder`,
    { method: "POST", json: { items } },
  );
}

export const PAYMENT_METHOD_LABEL: Record<PaymentMethod, string> = {
  CASH: "Наличные",
  CARD: "Карта",
  PAYKEEPER: "Paykeeper",
  SBP: "СБП",
  BANK_TRANSFER: "Безналичный",
};

export const PAYMENT_STATUS_LABEL: Record<PaymentStatus, string> = {
  PENDING: "Ожидание",
  COMPLETED: "Получен",
  FAILED: "Ошибка",
  REFUNDED: "Возвращён",
};

/**
 * Названия перевозчиков — зеркало `apps/api/src/carriers/carrier-names.ts`.
 * Порядок ключей = порядок пунктов в select'ах (интеграция, потом ручные).
 */
export const SHIPMENT_CARRIER_LABEL: Record<ShipmentCarrier, string> = {
  PICKUP: "Самовывоз",
  SDEK: "СДЭК",
  POCHTA: "Почта России",
  YANDEX: "Яндекс Доставка",
  PEK: "ПЭК",
  OZON: "Ozon",
  // 5Post — пункты выдачи X5 («Пятёрочка»/«Перекрёсток»); пишем так, как
  // перевозчик называет себя сам.
  FIVE_POST: "5Post",
  CUSTOM: "Своя доставка",
};

/**
 * Перевозчики без интеграции: трек-номер и стоимость менеджер вводит руками.
 * Самовывоз сюда не входит — там везти нечего, трека не бывает.
 */
export const MANUAL_TRACK_CARRIERS: readonly ShipmentCarrier[] = [
  "POCHTA",
  "YANDEX",
  "PEK",
  "OZON",
  "FIVE_POST",
  "CUSTOM",
];

/** Нужно ли менеджеру вводить трек-номер руками для этого перевозчика. */
export function isManualTrackCarrier(carrier: ShipmentCarrier): boolean {
  return MANUAL_TRACK_CARRIERS.includes(carrier);
}

export const SHIPMENT_STATUS_LABEL: Record<ShipmentStatus, string> = {
  PENDING: "Ожидает отгрузки",
  IN_TRANSIT: "В пути",
  DELIVERED: "Доставлено",
  RETURNED: "Возвращено",
};

export const RETURN_TYPE_LABEL: Record<ReturnType, string> = {
  DEFECT: "Брак",
  CHANGED_MIND: "Передумал",
  WRONG_ITEM: "Не тот товар",
  DAMAGED_IN_TRANSIT: "Повреждено в пути",
};

export const RETURN_STATUS_LABEL: Record<ReturnStatus, string> = {
  REQUESTED: "Запрошен",
  APPROVED: "Одобрен",
  AWAITING_GOODS: "Ждём товар",
  INSPECTION: "Экспертиза",
  APPROVED_REFUND: "К возврату средств",
  REJECTED: "Отклонён",
  COMPLETED: "Завершён",
};

export const ORDER_SOURCE_LABEL: Record<OrderSource, string> = {
  CRM_MANUAL: "CRM (вручную)",
  SITE: "Сайт",
  TELEGRAM: "Telegram",
  AVITO: "Avito",
  VK: "ВКонтакте",
  PHONE: "Телефон",
};

/**
 * Способ оплаты внутри строки-сводки — со строчной буквы («оплачен · наличные»).
 * Аббревиатуры и бренды остаются как есть.
 */
export const PAYMENT_METHOD_INLINE_LABEL: Record<PaymentMethod, string> = {
  CASH: "наличные",
  CARD: "карта",
  PAYKEEPER: "Paykeeper",
  SBP: "СБП",
  BANK_TRANSFER: "безналичный расчёт",
};

export const ORDER_DELIVERY_KIND_LABEL: Record<OrderDeliveryKind, string> = {
  PVZ: "ПВЗ",
  // Транспортная компания и курьер магазина — разные вещи и для менеджера, и
  // для клиента: «курьер» у заказа Почтой России читался как наш курьер.
  CARRIER: "доставка ТК",
  COURIER: "курьер магазина",
  PICKUP: "Самовывоз",
};

export const ORDER_DELIVERY_CARRIER_LABEL: Record<OrderDeliveryCarrier, string> = {
  SDEK: "СДЭК",
};

export const ORDER_DELIVERY_SHIPMENT_STATUS_LABEL: Record<
  OrderDeliveryShipmentStatus,
  string
> = {
  NONE: "отгрузок нет",
  DRAFT: "черновик отгрузки",
  HANDED: "передана, обрабатывается",
  IN_TRANSIT: "в пути",
  DELIVERED: "доставлена",
};

export const ORDER_DOCUMENT_STATUS_LABEL: Record<OrderDocumentStatus, string> = {
  NONE: "не создан",
  DRAFT: "черновик",
  ISSUED: "выпущен",
  SIGNED: "подписан",
};

/** Символ валюты для карточек; неизвестный код показываем как есть. */
export const CURRENCY_SYMBOL: Record<string, string> = {
  RUB: "₽",
  USD: "$",
  EUR: "€",
};

export function currencySymbol(code: string | null | undefined): string {
  if (!code) return "₽";
  return CURRENCY_SYMBOL[code.toUpperCase()] ?? code;
}

/**
 * Деньги для экрана: «4 820,00», «450,55».
 *
 * Копейки печатаем ВСЕГДА (находка ревью 5.1): «450» и «450,55» в одной
 * колонке читаются как разные единицы, а сумма заказа и сумма платежа рядом
 * обязаны выглядеть одинаково. Разделитель тысяч — неразрывный пробел
 * локали `ru-RU`.
 */
export function formatMoney(value: string | null): string {
  if (!value) return "—";
  const num = Number(value);
  if (isNaN(num)) return value;
  return num.toLocaleString("ru-RU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
